import { registerSW } from "virtual:pwa-register";
import { logError } from "./hooks/useErrorLogging";
import { isDevEnvironment } from "./lib/environment";

export function initPWA() {
    // Disable PWA logic completely on native platforms (Android/iOS)
    // as they are handled via the native app store updates and don't need SW refreshes
    if ((window as any).Capacitor?.isNativePlatform()) {
        console.log('[PWA] Native platform detected, skipping Service Worker registration');
        return;
    }

    if (import.meta.env.DEV || isDevEnvironment()) {
        // Avoid SW caching in dev/preview (can trap old bundles and make the preview look stale).
        const cleanupKey = 'preview-sw-cleanup-reloaded';
        console.log('[PWA] Dev/preview environment detected, skipping Service Worker registration');

        Promise.all([
            'serviceWorker' in navigator
                ? navigator.serviceWorker.getRegistrations().then(async (registrations) => {
                    await Promise.all(registrations.map((registration) => registration.unregister()));
                    return registrations.length > 0;
                })
                : Promise.resolve(false),
            'caches' in window
                ? caches.keys().then(async (cacheNames) => {
                    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
                    return cacheNames.length > 0;
                })
                : Promise.resolve(false),
        ]).then(([hadServiceWorkers, hadCaches]) => {
            if (!hadServiceWorkers && !hadCaches) return;

            try {
                if (!sessionStorage.getItem(cleanupKey)) {
                    sessionStorage.setItem(cleanupKey, '1');
                    window.location.reload();
                }
            } catch {
                window.location.reload();
            }
        }).catch((error) => {
            console.warn('[PWA] Failed to clean preview caches:', error);
        });

        try {
            localStorage.removeItem('ack-build');
        } catch {
            // Ignore storage access issues in preview cleanup path.
        }

        try {
            localStorage.removeItem('app-build-id');
        } catch {
            // Ignore storage access issues in preview cleanup path.
        }
    } else {
        const ACK_KEY = 'ack-build';
        const currentBuild = typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : 'unknown';

        const updateSW = registerSW({
            immediate: true,
            onRegisteredSW(swUrl, registration) {
                console.log('[SW] Registered:', swUrl);
                console.log('Service worker registered', {
                    type: 'pwa_diagnostic',
                    event: 'sw_registered',
                    swUrl,
                    scope: registration?.scope,
                });

                // Check for updates periodically (every 15 minutes)
                setInterval(() => {
                    registration?.update().catch((err) => {
                        console.debug('[SW] Background update failed (likely offline):', err);
                    });
                }, 15 * 60 * 1000);
            },
            onRegisterError(error) {
                console.error('[SW] Registration failed:', error);
                logError('Service worker registration failed', {
                    type: 'pwa_diagnostic',
                    event: 'sw_error',
                    errorMessage: error instanceof Error ? error.message : String(error),
                    errorStack: error instanceof Error ? error.stack : undefined,
                });
            },
            onNeedRefresh() {
                // Check if the user already acknowledged this exact build
                const ackedBuild = localStorage.getItem(ACK_KEY);
                if (ackedBuild === currentBuild) {
                    // Same build already applied — silently activate the SW without bothering the user
                    console.log('[SW] Build already acknowledged, silently updating...');
                    updateSW(true);
                    return;
                }

                // Genuinely new build — notify the user once
                console.log('[SW] New version available, notifying UI...');
                localStorage.setItem(ACK_KEY, currentBuild);

                window.dispatchEvent(new CustomEvent('app-update-available', {
                    detail: {
                        updateServiceWorker: async () => {
                            sessionStorage.setItem('app-just-updated', 'true');
                            await updateSW(true);
                        }
                    }
                }));
            },
        });
    }
}
